import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // Full details go to the console so we can diagnose stack overflows /
    // infinite render loops precisely.
    console.error('[AuthErrorBoundary] caught error:', error);
    console.error('[AuthErrorBoundary] error.stack:', error?.stack);
    console.error('[AuthErrorBoundary] componentStack:', errorInfo?.componentStack);
    try {
      (window as any).__lastAuthErrorInfo = { message: error?.message, stack: error?.stack, componentStack: errorInfo?.componentStack };
    } catch {}
  }

  render() {
    if (this.state.hasError) {
      const componentStack = (typeof window !== 'undefined' && (window as any).__lastAuthErrorInfo?.componentStack) || '';
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-2xl w-full text-center p-6">
            <h2 className="text-2xl font-bold text-destructive mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-4 text-sm">
              {this.state.error?.message || 'Unknown error'}
            </p>
            <details className="text-left bg-muted rounded p-3 mb-4" open>
              <summary className="cursor-pointer text-xs font-medium mb-2">Error details (share this with support)</summary>
              <pre className="text-[10px] leading-tight overflow-auto max-h-64 whitespace-pre-wrap">
{this.state.error?.stack || this.state.error?.message}
{componentStack ? `\n\nComponent stack:${componentStack}` : ''}
              </pre>
            </details>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
              >
                Reload Page
              </button>
              <button
                onClick={() => { window.location.href = '/admin'; }}
                className="px-4 py-2 border border-input rounded hover:bg-accent"
              >
                Back to Admin Login
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;